import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
              <i className="ri-file-search-line text-2xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-4">该文章可能已被删除或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '八戒电影网',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
      <Navbar />
      <main className="pt-16">
        <article>
          <div className="w-full bg-background-100 border-b border-background-200/70">
            <div className="w-full px-4 md:px-6 py-8 md:py-10">
              <div className="max-w-3xl mx-auto">
                <Breadcrumb
                  items={[
                    { label: '首页', to: '/' },
                    { label: '影视资讯', to: '/article/1' },
                    { label: article.title },
                  ]}
                />
                <div className="flex flex-wrap gap-2 mb-4">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-foreground-400 text-xs">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views.toLocaleString()} 次阅读
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full bg-background-50 py-8 md:py-12">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <div className="rounded-lg overflow-hidden mb-8">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-auto object-cover"
                  />
                </div>

                <div
                  className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="w-full bg-background-100 py-10 md:py-14 border-t border-background-200/70">
              <div className="w-full px-4 md:px-6">
                <div className="max-w-3xl mx-auto">
                  <h2 className="font-heading text-lg font-bold text-foreground-900 mb-5">
                    相关文章
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.id}`}
                        className="group block p-4 rounded-lg bg-background-50 hover:bg-background-200/50 transition-colors cursor-pointer"
                      >
                        <div className="rounded-lg overflow-hidden mb-3 aspect-[16/10]">
                          <img
                            src={ra.image}
                            alt={ra.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <h3 className="font-heading font-semibold text-foreground-900 text-sm group-hover:text-primary-500 transition-colors line-clamp-2">
                          {ra.title}
                        </h3>
                        <p className="text-foreground-400 text-xs mt-2">{ra.date}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}